import React, { useState, useEffect, useCallback } from "react";
import { Select, Button, Table, Modal, Form, Input, Empty, message, Tooltip, Tag, Spin, Row, Col } from "antd";
import { PlusOutlined, EditOutlined, DeleteOutlined, MinusCircleOutlined } from "@ant-design/icons";
import {
  getOrgList,
  getKpiFieldDetailsByOrgId,
  getKpiTemplates,
  getKpiTemplateFields,
  addKpiTemplate,
  updateKpiTemplate,
  deleteKpiTemplate,
  addKpiField,
  updateKpiField,
  deleteKpiField,
} from "./educationApi";

const { Option } = Select;
const { confirm } = Modal;

export const KPI_FREQUENCIES = [
  { value: "weekly", label: "Weekly" },
  { value: "monthly", label: "Monthly" },
  { value: "quarterly", label: "Quarterly" },
];
export const frequencyLabel = (value) => (KPI_FREQUENCIES.find((f) => f.value === value) || KPI_FREQUENCIES[1]).label;

// the group's KPI rows that are in no template, used when no template is chosen
export const EXISTING_ROWS = "existing";

const labelStyle = { marginBottom: 4, fontSize: 13, color: "#5B6472" };
const sectionTitle = { fontWeight: 600, fontSize: 15, margin: "8px 0 12px" };
const headStyle = { fontSize: 12, fontWeight: 600, color: "#5B6472", marginBottom: 6 };
const listOf = ({ error, result }) => (!error && result?.success !== false && Array.isArray(result?.result) ? result.result : []);
const ok = ({ error, result }) => !error && result?.success;
const emptyRow = { measure_title: "", measure_data: "", target: "" };
const clean = (value) => String(value || "").trim();

// Section headings: every row has one on screen, but the report (PDF, app)
// prints a stored heading above its row, so it is stored only on the first
// row of a run of rows with the same heading. Loading fills the rest of the
// run back in from the row above.
export const withHeadings = (rows) => {
  let heading = "";
  return rows.map((r) => {
    heading = clean(r.measure_title) || heading;
    return { ...r, measure_title: heading };
  });
};
const storedHeading = (rows, index) => (index > 0 && rows[index - 1].measure_title === rows[index].measure_title ? "" : rows[index].measure_title);

// a row with anything typed in it needs both its heading and its measure
const requiredInRow = (form, name, label) => ({
  validator: (_, value) => {
    const row = form.getFieldValue(["rows", name]) || {};
    const used = clean(row.measure_title) || clean(row.measure_data) || clean(row.target);
    return !used || clean(value) ? Promise.resolve() : Promise.reject(new Error(`Please enter the ${label}.`));
  },
});

// One modal for a whole template: its name, frequency and every KPI row (a
// row is its section heading, the measure and its target; whoever fills in
// the report adds Actual and Result). Save stores all of it. For the group's
// existing rows (no template) only the rows show.
const KpiTemplateModal = ({ visible, orgId, template, existing, onClose, onSaved }) => {
  const [form] = Form.useForm();
  const [original, setOriginal] = useState([]); // the rows as loaded, to see what changed
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const editing = Boolean(template || existing);

  useEffect(() => {
    if (!visible) return;
    form.resetFields();
    setOriginal([]);
    if (!editing) {
      form.setFieldsValue({ name: "", frequency: "monthly", rows: [{ ...emptyRow }, { ...emptyRow }, { ...emptyRow }] });
      return;
    }
    (async () => {
      setLoading(true);
      const rows = listOf(existing ? await getKpiFieldDetailsByOrgId(orgId) : await getKpiTemplateFields(template.id));
      // original: as stored (heading only on the first row of a run); the form: every row's heading
      const loaded = rows.map((r) => ({ id: r.id, measure_title: clean(r.measure_title), measure_data: r.measure_data || "", target: r.target || "" }));
      setOriginal(loaded);
      form.setFieldsValue({ name: template?.name || "", frequency: template?.frequency || "monthly", rows: loaded.length ? withHeadings(loaded) : [{ ...emptyRow }] });
      setLoading(false);
    })();
  }, [visible, editing, existing, template, orgId, form]);

  const handleOk = async () => {
    const values = await form.validateFields();
    // empty rows are left out; a heading is stored once per run of rows sharing it
    const filled = (values.rows || [])
      .map((r) => ({ id: r.id, measure_title: clean(r.measure_title), measure_data: clean(r.measure_data), target: clean(r.target) }))
      .filter((r) => r.measure_title || r.measure_data || r.target);
    if (!filled.length) return message.error("Please add at least one KPI row.");
    const rows = filled.map((r, i) => ({ ...r, measure_title: storedHeading(filled, i) }));

    setSaving(true);
    let templateId = template?.id || null;
    if (!existing) {
      const header = { name: clean(values.name), frequency: values.frequency };
      const response = template ? await updateKpiTemplate(template.id, header) : await addKpiTemplate({ org_id: orgId, ...header });
      if (!ok(response)) {
        setSaving(false);
        return message.error(response.result?.message || "The KPI template could not be saved. Please try again.");
      }
      templateId = template ? template.id : response.result?.result?.id;
    }

    // rows: removed ones deleted, changed ones updated, new ones added one by
    // one in order (the report lists rows in the order they were added)
    const kept = new Set(rows.filter((r) => r.id).map((r) => r.id));
    const before = Object.fromEntries(original.map((r) => [r.id, r]));
    const calls = original.filter((r) => !kept.has(r.id)).map((r) => () => deleteKpiField(r.id, orgId));
    rows.forEach(({ id, ...row }) => {
      const body = { org_id: orgId, kpi_template_id: existing ? null : templateId, ...row };
      if (!id) calls.push(() => addKpiField(body));
      else if (["measure_title", "measure_data", "target"].some((k) => clean(before[id]?.[k]) !== row[k])) calls.push(() => updateKpiField(id, body));
    });
    const failed = [];
    for (const call of calls) {
      const response = await call();
      if (!ok(response)) failed.push(response);
    }
    setSaving(false);

    if (failed.length) message.error(`${failed.length} KPI row${failed.length === 1 ? "" : "s"} could not be saved: ${failed[0].result?.message || "please try again"}`);
    else message.success(existing ? "KPI rows saved." : template ? "KPI template updated." : "KPI template added.");
    onSaved(existing ? EXISTING_ROWS : templateId);
    if (!failed.length) onClose();
  };

  const title = existing ? "Existing KPI Rows" : template ? "Edit KPI Template" : "New KPI Template";

  return (
    <Modal title={title} visible={visible} onCancel={onClose} onOk={handleOk} okText="Save" confirmLoading={saving} maskClosable={false} destroyOnClose width={960}>
      <Spin spinning={loading}>
        <Form form={form} layout="vertical">
          {!existing ? (
            <Row gutter={16}>
              <Col xs={24} md={16}>
                <Form.Item name="name" label="Template Name" rules={[{ required: true, whitespace: true, message: "Please enter the template name." }]}>
                  <Input maxLength={100} placeholder="e.g. Weekly Cleaning KPIs" />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item name="frequency" label="Frequency" tooltip="How often a facility fills in this KPI report. Weekly reports cover Monday to Sunday.">
                  <Select>
                    {KPI_FREQUENCIES.map((f) => (
                      <Option key={f.value} value={f.value}>
                        {f.label}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>
          ) : (
            <div style={{ fontSize: 13, color: "#5B6472", marginBottom: 12 }}>The group's KPI rows used when a report is made without a template (monthly).</div>
          )}

          <div style={{ fontWeight: 600, marginBottom: 4 }}>KPI Rows</div>
          <div style={{ fontSize: 12, color: "#5B6472", marginBottom: 10 }}>
            One row per measure. Rows that follow each other with the same section heading are grouped under it in the report. Empty rows are ignored.
          </div>
          <Row gutter={8}>
            <Col span={7}><div style={headStyle}>Section Heading <span style={{ color: "#B42318" }}>*</span></div></Col>
            <Col span={11}><div style={headStyle}>Measure <span style={{ color: "#B42318" }}>*</span></div></Col>
            <Col span={4}><div style={headStyle}>Target</div></Col>
          </Row>
          <Form.List name="rows">
            {(items, { add, remove }) => (
              <>
                <div style={{ maxHeight: 380, overflowY: "auto", overflowX: "hidden" }}>
                  {items.map(({ key, name, ...rest }) => (
                    <Row gutter={8} key={key} align="top">
                      <Form.Item {...rest} name={[name, "id"]} hidden>
                        <Input />
                      </Form.Item>
                      <Col span={7}>
                        <Form.Item {...rest} name={[name, "measure_title"]} style={{ marginBottom: 8 }} dependencies={[["rows", name, "measure_data"], ["rows", name, "target"]]} rules={[requiredInRow(form, name, "section heading")]}>
                          <Input maxLength={300} placeholder="e.g. Cleaning Audits" aria-label="Section heading" />
                        </Form.Item>
                      </Col>
                      <Col span={11}>
                        <Form.Item {...rest} name={[name, "measure_data"]} style={{ marginBottom: 8 }} dependencies={[["rows", name, "measure_title"], ["rows", name, "target"]]} rules={[requiredInRow(form, name, "measure")]}>
                          <Input.TextArea autoSize={{ minRows: 1, maxRows: 3 }} maxLength={1000} placeholder="e.g. Number of cleaning audits completed" />
                        </Form.Item>
                      </Col>
                      <Col span={4}>
                        <Form.Item {...rest} name={[name, "target"]} style={{ marginBottom: 8 }}>
                          <Input maxLength={45} placeholder="e.g. 100%" />
                        </Form.Item>
                      </Col>
                      <Col span={2}>
                        <Tooltip title="Remove row">
                          <Button type="text" danger icon={<MinusCircleOutlined />} onClick={() => remove(name)} aria-label="Remove row" />
                        </Tooltip>
                      </Col>
                    </Row>
                  ))}
                </div>
                {/* a new row starts in the last row's section */}
                <Button
                  type="dashed"
                  block
                  icon={<PlusOutlined />}
                  onClick={() => {
                    const rows = form.getFieldValue("rows") || [];
                    add({ ...emptyRow, measure_title: clean(rows[rows.length - 1]?.measure_title) });
                  }}
                  style={{ marginTop: 4 }}
                >
                  Add Row
                </Button>
              </>
            )}
          </Form.List>
        </Form>
      </Spin>
    </Modal>
  );
};

// Employee Connect > KPI > KPI Setup: a group's KPI templates (name,
// frequency and rows, all set in one modal). The group's older rows outside
// any template are not shown here; the mobile app still uses them when no
// template is chosen.
const KpiSetup = () => {
  const [orgs, setOrgs] = useState([]);
  const [orgId, setOrgId] = useState(undefined);
  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState(undefined);
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState({ visible: false, template: null });

  useEffect(() => {
    (async () => setOrgs(listOf(await getOrgList())))();
  }, []);

  // the group's templates; keeps the chosen one, else picks the first
  const loadTemplates = useCallback(async (pick) => {
    if (!orgId) return;
    const list = listOf(await getKpiTemplates(orgId));
    setTemplates(list);
    setTemplateId((current) => {
      const wanted = pick !== undefined ? pick : current;
      return list.some((t) => t.id === wanted) ? wanted : list[0]?.id;
    });
  }, [orgId]);

  const loadFields = useCallback(async () => {
    if (!orgId || !templateId) return setFields([]);
    setLoading(true);
    setFields(withHeadings(listOf(await getKpiTemplateFields(templateId))));
    setLoading(false);
  }, [orgId, templateId]);

  useEffect(() => {
    setTemplateId(undefined);
    loadTemplates();
  }, [loadTemplates]);

  useEffect(() => {
    loadFields();
  }, [loadFields]);

  const template = templates.find((t) => t.id === templateId);

  const removeTemplate = () =>
    confirm({
      title: "Delete KPI template",
      content: `Delete "${template.name}" and its KPI rows? Reports already made keep their PDF.`,
      okText: "Delete",
      okType: "danger",
      onOk: async () => {
        const response = await deleteKpiTemplate(template.id);
        if (!ok(response)) return message.error(response.result?.message || "The KPI template could not be deleted.");
        message.success("KPI template deleted.");
        loadTemplates(null);
      },
    });

  const columns = [
    { title: "Section Heading", dataIndex: "measure_title", key: "measure_title", width: "28%", render: (v) => v || "-" },
    { title: "Measure", dataIndex: "measure_data", key: "measure_data", render: (v) => v || "-" },
    { title: "Target", dataIndex: "target", key: "target", width: 140, render: (v) => v || "-" },
  ];

  return (
    <div>
      <div style={{ display: "flex", gap: 16, alignItems: "flex-end", flexWrap: "wrap", marginBottom: 16 }}>
        <div>
          <div style={labelStyle}>Group</div>
          <Select showSearch optionFilterProp="children" placeholder="Select group" style={{ width: 240 }} value={orgId} onChange={setOrgId}>
            {orgs.map((o) => (
              <Option key={o.org_id ?? o.id} value={o.org_id ?? o.id}>
                {o.org_name ?? o.name}
              </Option>
            ))}
          </Select>
        </div>
        {orgId ? (
          <>
            <div>
              <div style={labelStyle}>KPI Template</div>
              <Select style={{ width: 280 }} placeholder="No templates yet" value={templateId} onChange={setTemplateId} disabled={!templates.length}>
                {templates.map((t) => (
                  <Option key={t.id} value={t.id}>
                    {t.name} · {frequencyLabel(t.frequency)}
                  </Option>
                ))}
              </Select>
            </div>
            <Button icon={<PlusOutlined />} type="primary" onClick={() => setModal({ visible: true, template: null })}>
              New Template
            </Button>
          </>
        ) : null}
      </div>

      {!orgId ? (
        <Empty description="Select a group to set up its KPI templates." />
      ) : !template ? (
        <Empty description='No KPI templates for this group yet. Use "New Template" to create one.' />
      ) : (
        <>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
            <div style={sectionTitle}>
              {template.name} <Tag color="blue">{frequencyLabel(template.frequency)}</Tag>
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <Button icon={<EditOutlined />} onClick={() => setModal({ visible: true, template })}>
                Edit Template
              </Button>
              <Button danger icon={<DeleteOutlined />} onClick={removeTemplate}>
                Delete Template
              </Button>
            </div>
          </div>
          <Table rowKey="id" size="small" columns={columns} dataSource={fields} loading={loading} pagination={false} locale={{ emptyText: 'No KPI rows yet. Use "Edit Template" to add them.' }} />
        </>
      )}

      <KpiTemplateModal
        visible={modal.visible}
        orgId={orgId}
        template={modal.template}
        existing={false}
        onClose={() => setModal({ visible: false, template: null })}
        onSaved={(id) => {
          loadTemplates(id);
          if (id === templateId) loadFields();
        }}
      />
    </div>
  );
};

export default KpiSetup;
